import { Link } from 'react-router-dom';
import { useCarrinhoStore } from '@/stores/carrinhoStore';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

import { Trash2, Plus, Minus, ShoppingCart, ArrowRight, Truck } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { motion } from 'framer-motion';

export function Carrinho() {
  const {
    items,
    atualizarQuantidade,
    removerItem,
    subtotal,
    total,
    cupomCodigo,
    desconto,
    limparCarrinho,
  } = useCarrinhoStore();

  if (items.length === 0) {
    return (
      <div className="py-20 text-center">
        <ShoppingCart className="mx-auto h-16 w-16 text-fort3d-cinza-claro mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Seu carrinho está vazio</h1>
        <p className="text-fort3d-cinza-claro mb-6">Adicione produtos para continuar</p>
        <Link to="/catalogo">
          <Button variant="nuvem">Ver Catálogo</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="mx-auto max-w-7xl px-4">
        <h1 className="text-3xl font-bold text-white mb-2">Carrinho</h1>
        
        {/* Barra de Frete Grátis */}
        <div className="mb-8 bg-fort3d-cinza-escuro/50 border border-fort3d-cinza-medio rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Truck className={`w-5 h-5 ${subtotal() >= 250 ? 'text-fort3d-verde' : 'text-fort3d-laranja'}`} />
              <span className="text-sm font-medium text-white">
                {subtotal() >= 250 
                  ? 'Parabéns! Você ganhou Frete Grátis' 
                  : `Faltam ${formatCurrency(250 - subtotal())} para Frete Grátis`}
              </span>
            </div>
            <span className="text-xs text-fort3d-cinza-claro font-bold">Meta: R$ 250,00</span>
          </div>
          <div className="h-2 bg-fort3d-cinza-medio rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((subtotal() / 250) * 100, 100)}%` }}
              className={`h-full ${subtotal() >= 250 ? 'bg-fort3d-verde shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-fort3d-laranja'}`}
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <Card key={item.produto.id}>
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="h-20 w-20 rounded-lg bg-fort3d-cinza-medio flex items-center justify-center flex-shrink-0">
                    {item.produto.fotos?.[0] ? (
                      <img
                        src={item.produto.fotos[0]}
                        alt={item.produto.nome}
                        className="h-full w-full rounded-lg object-cover"
                      />
                    ) : (
                      <ShoppingCart className="h-8 w-8 text-fort3d-cinza-claro" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/produto/${item.produto.slug}`}
                      className="font-medium text-white hover:text-fort3d-laranja transition-colors line-clamp-1"
                    >
                      {item.produto.nome}
                    </Link>
                    <p className="text-fort3d-laranja font-medium">
                      {formatCurrency(item.produto.precoVenda)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => atualizarQuantidade(item.produto.id, item.quantidade - 1)}
                      className="h-8 w-8 rounded-lg border border-fort3d-cinza-medio bg-fort3d-cinza-escuro text-white hover:bg-fort3d-cinza-medio flex items-center justify-center"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center text-white font-medium">{item.quantidade}</span>
                    <button
                      onClick={() => atualizarQuantidade(item.produto.id, item.quantidade + 1)}
                      className="h-8 w-8 rounded-lg border border-fort3d-cinza-medio bg-fort3d-cinza-escuro text-white hover:bg-fort3d-cinza-medio flex items-center justify-center"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <button
                    onClick={() => removerItem(item.produto.id)}
                    className="p-2 text-fort3d-cinza-claro hover:text-fort3d-vermelho transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </CardContent>
              </Card>
            ))}

            <button
              onClick={limparCarrinho}
              className="text-sm text-fort3d-cinza-claro hover:text-fort3d-vermelho transition-colors"
            >
              Limpar carrinho
            </button>
          </div>

          {/* Resumo */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Resumo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between text-fort3d-cinza-claro">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal())}</span>
                </div>
                {desconto > 0 && (
                  <div className="flex justify-between text-fort3d-verde">
                    <span>Desconto ({cupomCodigo})</span>
                    <span>-{formatCurrency(desconto)}</span>
                  </div>
                )}
                <div className="border-t border-fort3d-cinza-medio pt-4">
                  <div className="flex justify-between text-lg font-bold text-white">
                    <span>Total</span>
                    <span>{formatCurrency(total())}</span>
                  </div>
                </div>
                <Link to="/checkout">
                  <Button variant="nuvem" className="w-full" size="lg" rightIcon={<ArrowRight className="h-4 w-4" />}>
                    Finalizar Compra
                  </Button>
                </Link>
                <Link to="/catalogo">
                  <Button variant="outline" className="w-full">
                    Continuar Comprando
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
